import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { claimSignupAttribution } from '@/lib/marketingTracking';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  markIntentionalSignOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const intentionalSignOut = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let initialized = false;

    const initialize = async () => {
      // Restore existing session FIRST, before any listeners
      const { data: { session: initialSession } } = await supabase.auth.getSession();
      if (isMounted) {
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        initialized = true;
        setLoading(false);
      }
    };

    // Set up auth state listener for SUBSEQUENT changes (sign in/out/token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted) return;

        // Ignore INITIAL_SESSION — we handle it via getSession() above
        if (event === 'INITIAL_SESSION') return;

        // Don't process events until getSession has resolved to avoid race conditions
        if (!initialized) return;

        if (event === 'SIGNED_OUT') {
          if (intentionalSignOut.current) {
            // User explicitly signed out
            setSession(null);
            setUser(null);
            intentionalSignOut.current = false;
          } else {
            // Transient failure (token refresh failed) — attempt recovery
            console.warn('Received SIGNED_OUT but logout was not intentional — attempting session recovery');
            try {
              const { data: { session: recoveredSession } } = await supabase.auth.getSession();
              if (recoveredSession && isMounted) {
                setSession(recoveredSession);
                setUser(recoveredSession.user);
                console.info('Session recovered successfully after transient SIGNED_OUT');
              } else if (isMounted) {
                // Session truly gone — clear state
                setSession(null);
                setUser(null);
              }
            } catch {
              // Recovery failed — clear state
              if (isMounted) {
                setSession(null);
                setUser(null);
              }
            }
          }
          return;
        }

        // For all other events (SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY)
        // update state with the new session
        if (newSession) {
          setSession(newSession);
          setUser(newSession.user);
          // Attribute the marketing session to this user (idempotent — RPC
          // uses COALESCE so re-runs on every sign-in never overwrite the
          // first-touch UTMs). Fires for both signup and signin so attribution
          // works for email/password AND OAuth.
          if (event === 'SIGNED_IN') {
            void claimSignupAttribution();
          }
        }
      }
    );

    initialize();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, name?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { name }
      }
    });
    // Fire-and-forget: mirror signup into Attio CRM
    if (!error) {
      supabase.functions.invoke('sync-to-attio-signup', {
        body: { email: email.trim().toLowerCase(), name, signupAt: new Date().toISOString() },
      }).catch(() => { /* never block signup */ });
    }
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string, _rememberMe = true) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Mark as intentional so the auth listener knows to clear state
    intentionalSignOut.current = true;
    // Clear local state immediately to prevent stale UI
    setSession(null);
    setUser(null);
    // Clear artifacts
    localStorage.removeItem('auth_remember');
    localStorage.removeItem('auth_remembered_email');
    sessionStorage.removeItem('sorvaine_subscription_status');
    sessionStorage.removeItem('corvence_subscription_status');
    // Remove session cookie
    document.cookie = 'auth_session=1; path=/; SameSite=Strict; max-age=0';
    // Then tell Supabase (may fail if session already expired, that's fine)
    try {
      await supabase.auth.signOut();
    } catch {
      // Session already gone — ignore
      intentionalSignOut.current = false;
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?reset=true`,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, resetPassword, markIntentionalSignOut: () => { intentionalSignOut.current = true; } }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
